import { useState, useEffect, useMemo } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import TextField from "../../../../components/Form/TextField";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import GraphicEqIcon from "@mui/icons-material/GraphicEq";
import AddIcon from "@mui/icons-material/Add";
import CardContainer from "../../../../components/Card/CardContainer";
import SectionHeader from "./SectionHeader";
import {
  SECTION_VOICE_TITLE,
  SECTION_VOICE_SUBTITLE,
  SECTION_VOICE_TONE_DESCRIPTION,
  SECTION_VOICE_ATTRIBUTES_LABEL,
} from "../../../../i18n/keysTranslations";
import { useTranslationApp } from "../../../../lib/i18next";
import {
  INPUT_COLOR_BORDER,
  CHIP_SEARCH_COMPONENT_COLOR,
} from "../../../../utils/colors";
import { BORDER_RADIUS } from "../../../../utils/constants";
import BrandMultilineField from "./BrandMultilineField";

export const VoiceSection = ({ formMethods, hasError = false }) => {
  const { t } = useTranslationApp();
  const [isEditing, setIsEditing] = useState(false);
  const [editedAttributes, setEditedAttributes] = useState([]);
  const [editedTone, setEditedTone] = useState("");

  const businessTextStyle = formMethods?.watch?.("businessTextStyle") || "";
  const watchedVoiceAttributes = formMethods?.watch?.(
    "businessVoiceAttributes"
  );
  const newAttribute = formMethods?.watch?.("newVoiceAttribute") || "";
  const businessVoiceAttributes = useMemo(() => {
    return Array.isArray(watchedVoiceAttributes) ? watchedVoiceAttributes : [];
  }, [watchedVoiceAttributes]);

  useEffect(() => {
    if (!isEditing && formMethods?.control) {
      setEditedTone(businessTextStyle);
      setEditedAttributes(businessVoiceAttributes);
    }
  }, [businessTextStyle, businessVoiceAttributes, isEditing, formMethods]);

  useEffect(() => {
    if (isEditing && formMethods?.control) {
      setEditedTone(businessTextStyle);
      setEditedAttributes(businessVoiceAttributes);
    }
  }, [isEditing, businessTextStyle, businessVoiceAttributes, formMethods]);

  if (!formMethods || !formMethods.control) {
    return null;
  }

  const { control, errors, setValue, validationRules } = formMethods;

  const removeAttribute = (attr) => {
    const updatedAttributes = editedAttributes.filter((a) => a !== attr);
    setEditedAttributes(updatedAttributes);
    if (isEditing) {
      setValue("businessVoiceAttributes", updatedAttributes);
    }
  };

  const addCustomAttribute = () => {
    const trimmedValue = newAttribute.trim();
    if (trimmedValue && editedAttributes.length < 6) {
      // Evitar duplicados
      if (!editedAttributes.includes(trimmedValue)) {
        const updatedAttributes = [...editedAttributes, trimmedValue];
        setEditedAttributes(updatedAttributes);
        setValue("newVoiceAttribute", "");
        if (isEditing) {
          setValue("businessVoiceAttributes", updatedAttributes);
        }
      }
    }
  };

  const onSave = () => {
    setValue("businessTextStyle", editedTone);
    setValue("businessVoiceAttributes", editedAttributes);
    setIsEditing(false);
  };

  const onEdit = async () => {
    setIsEditing(true);
    // Trigger validation when entering edit mode
    await formMethods.trigger?.("businessTextStyle");
  };

  return (
    <CardContainer title="" hasError={hasError}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          minHeight: "100%",
        }}
      >
        {/* Header */}
        <SectionHeader
          icon={GraphicEqIcon}
          title={SECTION_VOICE_TITLE}
          subtitle={SECTION_VOICE_SUBTITLE}
          isEditing={isEditing}
          onEdit={onEdit}
          onSave={onSave}
        />

        {/* Content */}
        <Box sx={{ flex: 1 }}>
          <Box
            sx={{
              display: isEditing ? "flex" : "none",
              flexDirection: "column",
              gap: 2.5,
            }}
          >
            {/* Attributes Section */}
            <Box>
              <Typography
                variant="body2"
                sx={{
                  color: "text.primary",
                  fontSize: "14px",
                  display: "block",
                  mb: 1.5,
                }}
              >
                {t(SECTION_VOICE_ATTRIBUTES_LABEL)}
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {/* Chips Row */}
                {editedAttributes.length > 0 && (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                    {editedAttributes.map((attr) => (
                      <Chip
                        key={attr}
                        label={attr}
                        onDelete={() => removeAttribute(attr)}
                        size="small"
                        sx={{
                          backgroundColor: CHIP_SEARCH_COMPONENT_COLOR,
                          border: `1px solid ${INPUT_COLOR_BORDER}`,
                          height: 32,
                          "& .MuiChip-label": {
                            px: 1.5,
                            py: 0.75,
                          },
                        }}
                      />
                    ))}
                  </Box>
                )}
                {/* Input and Button Row */}
                <Box sx={{ display: "flex", gap: 1 }}>
                  <TextField
                    variant="filled"
                    control={control}
                    name="newVoiceAttribute"
                    fullWidth
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        addCustomAttribute();
                      }
                    }}
                    disabled={editedAttributes.length >= 6}
                    rules={{ required: false }}
                  />
                  <IconButton
                    size="small"
                    onClick={addCustomAttribute}
                    disabled={
                      editedAttributes.length >= 6 || !newAttribute.trim()
                    }
                    sx={{
                      width: 36,
                      height: 36,
                      border: `1px solid ${INPUT_COLOR_BORDER}`,
                      borderRadius: BORDER_RADIUS,
                      color: "text.primary",
                      flexShrink: 0,
                      "&:hover": {
                        backgroundColor: "action.hover",
                      },
                      "&.Mui-disabled": {
                        opacity: 0.5,
                      },
                    }}
                  >
                    <AddIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Box>
              </Box>
            </Box>

            {/* Tone Description Section */}
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="body2"
                sx={{
                  color: "text.primary",
                  fontSize: "14px",
                  display: "block",
                  mb: 1.5,
                }}
              >
                {t(SECTION_VOICE_TONE_DESCRIPTION)}
              </Typography>
              <BrandMultilineField
                control={control}
                name="businessTextStyle"
                value={editedTone}
                actionsChange={(value) => setEditedTone(value)}
                rules={validationRules.businessTextStyle}
                errors={errors}
              />
            </Box>
          </Box>

          {!isEditing && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 2.5,
                flex: 1,
              }}
            >
              {/* Attributes Display */}
              {businessVoiceAttributes.length > 0 && (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                  {businessVoiceAttributes.map((attr) => (
                    <Chip
                      key={attr}
                      label={attr}
                      sx={{
                        backgroundColor: "#F5F5F5",
                        color: "text.primary",
                        border: "none",
                        fontSize: "14px",
                      }}
                    />
                  ))}
                </Box>
              )}

              {/* Tone Display */}
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: "text.secondary",
                    lineHeight: 1.6,
                    fontSize: "0.875rem",
                  }}
                >
                  {businessTextStyle}
                </Typography>
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    </CardContainer>
  );
};

export default VoiceSection;
